import { useFeatureIsOn } from '@growthbook/growthbook-react';
import moment from 'moment';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useFormContext, useFormState, useWatch } from 'react-hook-form';
import type { FieldErrors } from 'react-hook-form';
import { useSelector } from 'react-redux';

import { Spinner } from '@common/components/Spinner';
import { Routes } from '@common/constants';
import { normaliseGenderCode } from '@common/helpers/genderRestriction';
import {
  SPA_DAY_PARTY_CAPACITY,
  useGetSpaPackage,
  useGetSpaPackageConfigGroups,
  useGetSpaPackageTimeslots,
} from '@features/spa';
import {
  useBookingFormLabels,
  useCalendarNavigation,
  useGetSpaSettings,
  useSyncSpaDateToRedux,
} from '@features/spa/hooks';
import { useGetSpaPackageExtras } from '@features/spa/hooks/useGetSpaPackageExtras';
import { useTranslations } from '@lang';
import { basketSelectors } from '@store/basket/selectors';
import { yourJourneySelectors } from '@store/yourJourney/selectors';
import { buildExistingGuests } from '@utils/buildExistingGuests';
import { isIntegrationUnavailable } from '@utils/integrationErrorCode';
import { scrollToFirstError } from '@utils/scrollToFirstError';
import { Guest } from 'shared-types/Basket';
import { Property } from 'shared-types/Property';
import { SpaPackageType } from 'shared-types/SpaPackage';
import { useRouter } from 'shared-routing';

import { SpaPackageFormValues } from '../SpaPackageForm.schema';

import { SpaDaysBookingForm } from 'voyager';

import {
  entityHasAvailability,
  isBookableGroup,
  isBookableOption,
} from '../configGroupBookability';
import { getIncludedOptionsGenderRestriction } from './SpaDayBooking.helpers';
import { useArrivalTime } from './hooks/useArrivalTime';
import { useAutoAdvanceHiddenArrival } from './hooks/useAutoAdvanceHiddenArrival';
import { useConfigGroupHandlers } from './hooks/useConfigGroupHandlers';
import { useDateManagement } from './hooks/useDateManagement';
import { useGuestConfig } from './hooks/useGuestConfig';
import { usePreferences } from './hooks/usePreferences';
import { usePrefillTreatmentTimes } from './hooks/usePrefillTreatmentTimes';
import { useSeedForcedSelections } from './hooks/useSeedForcedSelections';
import { useSelectSoleTimeslot } from './hooks/useSelectSoleTimeslot';
import { useSpaPackageDateAvailability } from './hooks/useSpaPackageDateAvailability';
import { useSubmitBooking } from './hooks/useSubmitBooking';
import { useTotalPrice } from './hooks/useTotalPrice';

type SpaDayBookingProps = {
  id: number;
  property: Property;
  language: string;
  startDate: string;
  onClose: (showMiniBasket: boolean) => void;
  loggedInGuest?: Guest | null;
};

export const SpaDayBooking: FC<SpaDayBookingProps> = ({
  id,
  property,
  language,
  onClose,
  loggedInGuest,
}) => {
  const { t } = useTranslations();
  const router = useRouter();
  const isPrefillSpaDaySlotsEnabled = useFeatureIsOn('feature.prefill-spa-day-slots');
  const journeyParams = useSelector(yourJourneySelectors.getParsedJourneyParams);
  const isSubmitting = useSelector((state: Record<string, unknown>) =>
    basketSelectors.getIsBasketDetailsFetching(state)
  );
  const basketGuests = useSelector((state: any) => basketSelectors.getBasketGuests(state)) || [];

  const existingGuests = useMemo(
    () => buildExistingGuests(basketGuests, loggedInGuest),
    [basketGuests, loggedInGuest]
  );

  const form = useFormContext<SpaPackageFormValues>();
  const { errors: formErrors } = useFormState({ control: form.control });

  const { data: spaPackage, isLoading: isLoadingPackage } = useGetSpaPackage(
    property.id,
    id,
    language
  );
  const { data: spaSettings } = useGetSpaSettings(property.id);
  const showGenderNeutral = spaSettings?.showGenderNeutral || false;
  const hideArrivalTimes = spaSettings?.hideArrivalTimes || false;

  const entity = spaPackage as SpaPackageType | undefined;

  const watchedGuests = useWatch({ control: form.control, name: 'guests' });
  const watchedExtras = useWatch({ control: form.control, name: 'extras' });
  const watchedArrivalTime = useWatch({ control: form.control, name: 'arrivalTime' });
  const watchedDate = useWatch({ control: form.control, name: 'date' });

  const selectedDateString = (
    watchedDate || moment(journeyParams.spaDate).format('YYYY-MM-DD')
  ).trim();

  useSyncSpaDateToRedux(selectedDateString, journeyParams.spaDate);

  const {
    weekStartDate,
    setWeekStartDate,
    calendarView,
    setCalendarView,
    handleCalendarViewChange,
    debouncedWeekStartDate,
  } = useCalendarNavigation({
    initialDate: selectedDateString,
    form,
  });

  const {
    data: timeslots,
    isLoading: isLoadingTimeslots,
    isError: isTimeslotsError,
    error: timeslotsError,
    refetch: refetchTimeslots,
  } = useGetSpaPackageTimeslots(
    property.id,
    id,
    debouncedWeekStartDate,
    entity?.numberOfGuests,
    7,
    calendarView === 'week'
  );

  const {
    counter,
    reservation,
    calendar,
    timeslotChips,
    guestDetails,
    preferences,
    stickyFooterTotalLabel,
    telephoneOnly: telephoneOnlyLabels,
    unavailable: unavailableLabels,
    integrationUnavailable: integrationUnavailableLabels,
  } = useBookingFormLabels(language, {
    phoneNumber: spaSettings?.phoneNumber,
    entityName: entity?.name,
    spaEntityLabel: t('spa.spaDay').toLowerCase(),
    pluralEntityLabel: t('app.spaDays'),
    formattedDate: moment(selectedDateString).format('dddd Do MMMM YYYY'),
    onCtaClick: () => router.push({ pathname: Routes.SPA_DAY_LIST }),
    onRetryClick: () => {
      void refetchTimeslots();
    },
  });

  const { availability, handleDateSelect, handleWeekNavigate } = useDateManagement({
    form,
    selectedDateString,
    weekStartDate,
    setWeekStartDate,
    timeslots,
    hideArrivalTimes,
    calendarView,
    setCalendarView,
  });

  const supportsTimeslots = !!entity?.supportsTimeslots;

  const { isUnavailableForDate } = useSpaPackageDateAvailability({
    propertyId: property.id,
    packageId: id,
    date: selectedDateString,
    guests: journeyParams?.spaAdults,
    language,
    enabled: !!entity && !entity.telephoneOnly && !supportsTimeslots,
  });

  const unavailableForTimeslots = supportsTimeslots
    ? !isLoadingTimeslots &&
      calendarView === 'week' &&
      weekStartDate === debouncedWeekStartDate &&
      (isTimeslotsError ||
        (!!timeslots &&
          (!timeslots[selectedDateString] || timeslots[selectedDateString].length === 0)))
    : isUnavailableForDate;

  // Distinct from "no availability": the read failed, so nothing is known either way. Only the
  // timeslot branch can be unknown — the date branch guards on data, so it is silent on a failure
  // rather than wrong. This page drives its own calendar, so the flag is derived here rather than
  // coming out of useTimeslotAvailability.
  const integrationUnavailable = useMemo(
    () => supportsTimeslots && isIntegrationUnavailable(timeslotsError),
    [supportsTimeslots, timeslotsError]
  );

  const selectedTime = watchedArrivalTime?.startTime;

  const { selectedDateTimeSlots, arrivalTimeChips, handleArrivalTimeSelect, endTime } =
    useArrivalTime({
      form,
      timeslots,
      selectedDateString,
      selectedTime,
      hideArrivalTimes,
    });

  const { data: configurationGroups = [], isLoading: isLoadingConfigGroups } =
    useGetSpaPackageConfigGroups(
      entity?.configurationGroups || [],
      property.id,
      id,
      selectedDateString,
      watchedGuests?.length || 1,
      selectedTime,
      endTime,
      SPA_DAY_PARTY_CAPACITY
    );

  const isLoading = isLoadingPackage || isLoadingTimeslots || isLoadingConfigGroups;

  const hasConfigGroupData = !!selectedTime && configurationGroups.length > 0;

  const selectedSlotUnavailable =
    hasConfigGroupData &&
    !entityHasAvailability({ ...entity, configurationGroups } as SpaPackageType);

  const daySlots = timeslots?.[selectedDateString];

  const { noBookableSlot } = useAutoAdvanceHiddenArrival({
    form,
    hideArrivalTimes,
    daySlots,
    selectedTime,
    selectedSlotUnavailable,
    isCheckingSlot: isLoadingConfigGroups,
  });

  const unavailable =
    unavailableForTimeslots || (hideArrivalTimes ? noBookableSlot : selectedSlotUnavailable);

  const { data: extras = [] } = useGetSpaPackageExtras(
    property.id,
    id,
    language,
    selectedDateString
  );

  const { totalPrice, guestsCount } = useTotalPrice({
    entityPrice: entity?.price,
    selectedDateTimeSlots,
    selectedTime,
    watchedGuests,
    watchedExtras,
  });

  const discountedSelectedDateTimeSlots = useMemo(
    () =>
      selectedDateTimeSlots?.map((slot) => ({
        ...slot,
        price: slot.discount?.discountedPrice ?? slot.price,
      })),
    [selectedDateTimeSlots]
  );

  const { totalPrice: discountedTotalPrice } = useTotalPrice({
    entityPrice: entity?.discount?.discountedPrice,
    selectedDateTimeSlots: discountedSelectedDateTimeSlots,
    selectedTime,
    watchedGuests,
    watchedExtras,
  });

  const configGroupsNoExtras = useMemo(
    () =>
      configurationGroups
        .filter((cg) => !cg.isExtras && isBookableGroup(cg))
        .map((cg) => ({ ...cg, options: cg.options.filter(isBookableOption) })),
    [configurationGroups]
  );

  const requireTimeslots = !!entity?.configurationGroupsRequireTimeslots;

  useSeedForcedSelections({ form, configGroupsNoExtras, watchedGuests });
  useSelectSoleTimeslot({ form, configGroupsNoExtras, watchedGuests, requireTimeslots });

  usePrefillTreatmentTimes({
    enabled: isPrefillSpaDaySlotsEnabled,
    form,
    configGroupsNoExtras,
    watchedGuests,
    arrivalStartTime: selectedTime,
    selectedDate: selectedDateString,
    requireTimeslots: !!entity?.configurationGroupsRequireTimeslots,
  });

  const {
    titleOptions,
    genderOpts,
    guests,
    handleGuestCountChange,
    handleGuestChange,
    handleExistingGuestSelect,
  } = useGuestConfig({
    form,
    watchedGuests,
    configGroupsNoExtras,
    showGenderNeutral,
    genderSpecific:
      normaliseGenderCode(entity?.gender) ??
      getIncludedOptionsGenderRestriction(configGroupsNoExtras),
    existingGuests,
    guestsErrors: formErrors.guests as unknown as FieldErrors<
      SpaPackageFormValues['guests'][number]
    >[],
    requireTimeslots,
  });

  const [showArrivalTimeError, setShowArrivalTimeError] = useState(false);
  const hasArrivalTimeChips = (arrivalTimeChips?.length ?? 0) > 0;
  const awaitingArrivalTimeSelection = hasArrivalTimeChips && !selectedTime;

  useEffect(() => {
    if (!selectedTime) return;
    setShowArrivalTimeError(false);
    form.clearErrors();
  }, [selectedTime, form]);

  const arrivalTimeError = showArrivalTimeError
    ? t('error.fieldRequired', { label: t('spa.booking.timeslots.title') })
    : undefined;

  const {
    handleAllOptionsTimeslotChange,
    handleSingleOptionSelect,
    handleSingleOptionTimeslotChange,
    handleMultiOptionToggle,
    handleMultiOptionTimeslotChange,
  } = useConfigGroupHandlers({ form, configGroupsNoExtras });

  const { handleFreeTextChange, preferenceCategories } = usePreferences({ form });

  const hasExtras = extras.length > 0;

  const { handleSubmit, handleNextStep, handleBack } = useSubmitBooking({
    form,
    entity,
    propertyId: property.id,
    language,
    id,
    onClose,
    onInvalid: scrollToFirstError,
  });

  const handleCtaClick = useCallback(() => {
    if (awaitingArrivalTimeSelection) {
      setShowArrivalTimeError(true);
      scrollToFirstError();
      return;
    }
    if (hasExtras) {
      handleNextStep();
    } else {
      handleSubmit();
    }
  }, [awaitingArrivalTimeSelection, hasExtras, handleNextStep, handleSubmit, scrollToFirstError]);

  if (!entity) return null;

  return (
    <>
      <Spinner showDelayMessage isActive={isSubmitting} />
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleCtaClick();
        }}
        autoComplete="on"
      >
        <SpaDaysBookingForm
          imageUrl={entity.image?.url}
          imageAlt={entity.name}
          onBack={handleBack}
          backLabel={t('spa.booking.goBack')}
          progressStep={1}
          progressTotal={hasExtras ? 2 : undefined}
          isLoading={isLoading}
          guestCount={guestsCount}
          minGuests={entity.numberOfGuests}
          maxGuests={spaPackage?.maximumGuestsPerBasket || undefined}
          guestStep={entity.numberOfGuests}
          onGuestCountChange={handleGuestCountChange}
          activeDate={weekStartDate}
          selectedDate={selectedDateString}
          calendarView={calendarView}
          availability={calendarView === 'week' ? availability : undefined}
          onDateSelect={handleDateSelect}
          onWeekNavigate={handleWeekNavigate}
          onCalendarViewChange={handleCalendarViewChange}
          arrivalTimeSlots={arrivalTimeChips}
          selectedArrivalTime={selectedTime}
          onArrivalTimeSelect={handleArrivalTimeSelect}
          arrivalTimeError={arrivalTimeError}
          guests={guests}
          onExistingGuestSelect={handleExistingGuestSelect}
          onGuestChange={handleGuestChange}
          titleOptions={titleOptions}
          genderOptions={genderOpts}
          onAllOptionsTimeslotChange={handleAllOptionsTimeslotChange}
          onSingleOptionSelect={handleSingleOptionSelect}
          onSingleOptionTimeslotChange={handleSingleOptionTimeslotChange}
          onMultiOptionToggle={handleMultiOptionToggle}
          onMultiOptionTimeslotChange={handleMultiOptionTimeslotChange}
          preferenceCategories={preferenceCategories}
          onPreferenceOptionToggle={() => {}}
          onPreferenceOtherChange={() => {}}
          onPreferenceFreeTextChange={handleFreeTextChange}
          labels={{
            title: entity.name,
            subtitle: t('spa.booking.subtitle'),
            noteText: t('spa.booking.timesSubjectToChange'),
            noteDismissLabel: t('spa.booking.dismissNote'),
            counter,
            reservation,
            calendar,
            guestDetails,
            preferences,
            timeslotAriaLabelFormatter: (label: string) =>
              t('spa.booking.timeslotAriaLabel', { label }),
            timeslotChips: {
              ...timeslotChips,
              title: t('spa.package.arrivalTime'),
            },
            stickyFooter: {
              totalLabel: stickyFooterTotalLabel,
              ctaLabel: hasExtras ? t('spa.package.nextStep') : t('product.ctaButton'),
            },
            telephoneOnly: telephoneOnlyLabels,
            unavailable: integrationUnavailable ? integrationUnavailableLabels : unavailableLabels,
          }}
          telephoneOnly={entity.telephoneOnly || false}
          unavailable={unavailable}
          stickyFooter={
            !entity.telephoneOnly && !unavailable && totalPrice
              ? {
                  title: entity.name,
                  totalAmount: discountedTotalPrice
                    ? discountedTotalPrice.amount
                    : totalPrice.amount,
                  originalAmount: discountedTotalPrice ? totalPrice.amount : undefined,
                  currencyCode: totalPrice.currencyCode,
                  onCtaClick: handleCtaClick,
                }
              : undefined
          }
        />
      </form>
    </>
  );
};
